import React, { useMemo, useState } from 'react'
import { ClipLoader } from 'react-spinners'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import { DashboardData, OrganizationData } from '../types'
import * as echarts from 'echarts/core'

interface DashBoardStateProps {
  stats: DashboardData
  isApiRequestInProgress: any
}
interface Position {
  left?: number
  right?: number
  top?: number
  bottom?: number
}

interface Size {
  viewSize: any[]
}
type DashBoardProps = DashBoardStateProps

const WorksSummary = ({ stats, isApiRequestInProgress }: DashBoardProps): JSX.Element => {
  const handleTooltipPosition = (
    point: number[],
    params: object | any[],
    dom: HTMLElement,
    rect: object,
    size: Size,
  ): Position | null => {
    // only for mobile device
    if (window.innerWidth <= 540) {
      const tooltipHeight = dom.offsetHeight
      const obj: Position = { top: point[1] - tooltipHeight - 20 }

      const posProp = point[0] < size.viewSize[0] / 2 ? 'left' : 'right'

      obj[posProp] = 5

      return obj
    }

    return null
  }

  const totalWorkAmount: number = useMemo(() => {
    return stats.worksAwarded + stats.worksNotAwarded
  }, [stats.worksAwarded, stats.worksNotAwarded])

  const workItemAwardStatusChartOptions = useMemo(() => {
    return {
      tooltip: {
        trigger: 'item',
        padding: [7, 10],
        backgroundColor: '#eff2f6',
        borderColor: '#cbd0dd',
        textStyle: { color: '#141824' },
        borderWidth: 1,
        position: (point: number[], params: object | any[], dom: HTMLElement, rect: object, size: Size) =>
          handleTooltipPosition(point, params, dom, rect, size),
        transitionDuration: 0,
        formatter: (params: Record<string, any>) => {
          return `<strong>${params.seriesName}:</strong> ${params.value}%`
        },
      },
      legend: { show: false },
      series: [
        {
          type: 'gauge',
          center: ['50%', '60%'],
          name: 'Work item with an award',
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max: 100,
          splitNumber: 12,
          itemStyle: {
            color: '#3874ff',
          },
          progress: {
            show: true,
            roundCap: true,
            width: 24,
            itemStyle: {
              shadowBlur: 0,
              shadowColor: '#0000',
            },
          },
          pointer: {
            show: false,
          },
          axisLine: {
            roundCap: true,
            lineStyle: {
              width: 24,
              color: [[1, totalWorkAmount ? '#ffefca' : '#e5edff']],
            },
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            show: false,
          },
          title: {
            show: false,
          },
          detail: {
            show: false,
          },
          data: [
            {
              value: totalWorkAmount ? Math.round((stats.worksAwarded / totalWorkAmount) * 100 * 100) / 100 : 0,
            },
          ],
        },
      ],
    }
  }, [stats.worksAwarded, stats.worksNotAwarded, totalWorkAmount])
  return (
    <div className="col-12 col-md-12">
      <div className="card h-100">
        <div className="card-body d-flex flex-column">
          <div className="d-flex justify-content-between">
            <div>
              <h5 className="mb-2">Works Summary</h5>
              <h6 className="text-700">Work item with/without an award</h6>
            </div>
          </div>

          <div className="row g-3 align-items-center">
            <div className="col-sm-8 col-md-12 col-lg-8 col-xl-12 col-xxl-8">
              <div className="echarts-paying-customer-chart" style={{ height: '280px', width: '100%' }}>
                {isApiRequestInProgress ? (
                  <div className="spinner-wrapper mt-5">
                    <ClipLoader color="#e5780b" loading={isApiRequestInProgress} size={35} />
                  </div>
                ) : (
                  <ReactEChartsCore echarts={echarts} option={workItemAwardStatusChartOptions} />
                )}
              </div>
            </div>
            {isApiRequestInProgress ? null : (
              <div className="col-sm-4 col-md-12 col-lg-4 col-xl-12 col-xxl-4 d-flex justify-content-end-xxl mt-0">
                <div className="d-flex flex-1 justify-content-center d-sm-block d-md-flex d-lg-block d-xl-flex d-xxl-block">
                  <div className="mb-4 me-6 me-sm-0 me-md-6 me-lg-0 me-xl-6 me-xxl-0">
                    <div className="d-flex align-items-center mb-2">
                      <div className="fa-solid fa-circle text-primary me-2"></div>
                      <h6 className="flex-1 mb-0">with an award</h6>
                      <h6 className="text-900 fw-semi-bold mb-0">
                        {totalWorkAmount ? Math.round((stats.worksAwarded / totalWorkAmount) * 100 * 100) / 100 : 0}%
                      </h6>
                    </div>

                    <div className="d-flex align-items-center mb-2">
                      <div className="fa-solid fa-circle text-warning-100 me-2"></div>
                      <h6 className="flex-1 mb-0">without an award</h6>
                      <h6 className="text-900 fw-semi-bold mb-0">
                        {totalWorkAmount ? Math.round((stats.worksNotAwarded / totalWorkAmount) * 100 * 100) / 100 : 0}%
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default WorksSummary
